/* eslint-disable @typescript-eslint/no-explicit-any */
import { Helmet } from 'react-helmet-async';
import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Suspense, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import LocationOn from '@mui/icons-material/LocationOn';
import { CellContext, createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { useGetLocations } from 'api/hooks/useLocation';
import { Loading, LocationBar, Table } from 'components';
import { Location } from '__generated__/graphql';

function RowActions(props: CellContext<Location, unknown>) {
  const navigate = useNavigate();

  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => navigate(`${props.row.original.id}/update`)} aria-label="Edit">
        <PencilIcon className="w-6 h-6 text-gray-500 hover:text-primary-600" />
      </button>
    </div>
  );
}

function LocationList() {
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<Location>();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: () => 'Name',
        cell: (info: { renderValue: () => any }) => info.renderValue(),
        footer: (info: { column: { id: any } }) => info.column.id,
      }),

      columnHelper.display({
        id: 'actions',
        cell: RowActions,
      }),
    ] as Array<ColumnDef<Location, unknown>>;
  }, [columnHelper]);

  const { locations, locationsLoading } = useGetLocations();

  if (locationsLoading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet>
        <title>Location List</title>
      </Helmet>
      <LocationBar section="Location" page="Location List" Icon={LocationOn} />
      <div className="my-10 px-4 md:px-[5%]">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="text-display-lg leading-display-lg tracking-display-lg text-black font-medium">Location List</div>
          <button
            type="button"
            className="text-white bg-primary-500 rounded-lg px-5 py-2.5 font-semibold text-md leading-md hidden md:block"
            onClick={() => navigate('create')}
          >
            Add Location
          </button>
          <button
            type="button"
            aria-label="Add Location"
            className="text-white bg-primary-500 rounded-lg p-2 font-semibold text-md leading-md md:hidden"
            onClick={() => navigate('create')}
          >
            <PlusIcon className="w-5 h-5 text-white" />
          </button>
        </div>
        <Suspense fallback={<Loading />}>
          <Table<Location> csv={{ data: locations, fileName: 'locationsList' }} data={locations} columns={columns} />
        </Suspense>
      </div>
    </>
  );
}

export default LocationList;
